import type { App, EnvValueType, EnvVisibility, Platform } from '@hangar/shared';
import type { EnvVarCreateInput } from '@/api/endpoints';
import type { EnvVar } from '@/api/schemas';
import { isExpoPublic, needsApp, needsEnvironment, needsPlatform, type ScopeKey } from './envHelpers';
import type { ScopeSelection } from './ScopePicker';
import type { ValueState } from './ValueEditor';

/** Pure model of the "Add variable / Edit variable" form: initial values, key rules, validation and request bodies. */

export const KEY_RE = /^[A-Z_][A-Z0-9_]*$/;
export const VISIBILITIES: EnvVisibility[] = ['plain', 'sensitive', 'secret'];
export const VALUE_TYPES: EnvValueType[] = ['string', 'multiline', 'file', 'json'];
/** Visibility can only go up (plain < sensitive < secret). */
export const RANK: Record<EnvVisibility, number> = { plain: 0, sensitive: 1, secret: 2 };

export interface EnvVarFormValues {
  key: string;
  value: ValueState;
  visibility: EnvVisibility;
  valueType: EnvValueType;
  configTime: boolean;
  description: string;
  note: string;
  scopeKey: ScopeKey;
  appSlug: string;
  environment: string;
  platform: Platform | '';
}

const defaultVisibility = (scope: ScopeSelection): EnvVisibility => (scope.scope === 'org' ? 'sensitive' : 'plain');

const initialType = (editing: EnvVar | undefined): Pick<EnvVarFormValues, 'valueType' | 'configTime'> => ({ valueType: editing?.valueType ?? 'string', configTime: editing?.configTime ?? false });

/** Metadata of an existing variable, or the defaults of a new one (organization scope defaults to `sensitive`). */
function initialMetadata(editing: EnvVar | undefined, scope: ScopeSelection): Pick<EnvVarFormValues, 'key' | 'visibility' | 'valueType' | 'configTime' | 'description'> {
  return {
    key: editing?.key ?? '',
    visibility: editing?.visibility ?? defaultVisibility(scope),
    description: editing?.description ?? '',
    ...initialType(editing),
  };
}

/**
 * The form's first state. Editing keeps the variable's metadata; a new variable starts from the scope the page is
 * filtered by and the first app.
 */
export function initialEnvVarForm(editing: EnvVar | undefined, scope: ScopeSelection, apps: App[] | undefined): EnvVarFormValues {
  return {
    ...initialMetadata(editing, scope),
    value: { value: '' },
    note: '',
    scopeKey: scope.scope,
    appSlug: scope.appSlug ?? apps?.[0]?.slug ?? '',
    environment: scope.environment ?? '',
    platform: scope.platform ?? '',
  };
}

/** Keys are env var names: uppercase letters, digits and underscores; anything else becomes `_` as it is typed. */
export const normalizeKey = (raw: string): string => raw.toUpperCase().replace(/[^A-Z0-9_]/g, '_');

/**
 * Whether a visibility choice is locked: `secret` is never allowed for EXPO_PUBLIC_* (it is inlined into the bundle),
 * and an existing variable can only be raised.
 */
export function visibilityLocked(visibility: EnvVisibility, key: string, editing: EnvVar | undefined): boolean {
  if (isExpoPublic(key) && visibility === 'secret') return true;
  return editing ? RANK[visibility] < RANK[editing.visibility] : false;
}

export type EnvVarField = 'key' | 'visibility' | 'app' | 'environment' | 'platform' | 'value' | 'description';
export type EnvVarErrors = Record<EnvVarField, string | undefined>;

const keyError = (key: string): string | undefined => (!key ? 'Key is required' : !KEY_RE.test(key) ? 'Uppercase letters, digits and underscores' : undefined);

function visibilityError(values: EnvVarFormValues, editing: EnvVar | undefined): string | undefined {
  if (isExpoPublic(values.key) && values.visibility === 'secret') return 'EXPO_PUBLIC_* is inlined into the JS bundle: it can never be secret (use plain or sensitive)';
  return editing && RANK[values.visibility] < RANK[editing.visibility] ? 'Visibility can only be raised (create a new variable to lower it)' : undefined;
}

/** What a new variable still needs for its scope: the app, the environment, the platform (an edit never changes them). */
function scopeErrors(values: EnvVarFormValues): Pick<EnvVarErrors, 'app' | 'environment' | 'platform'> {
  const { scopeKey } = values;
  return {
    app: needsApp(scopeKey) && !values.appSlug ? 'Pick the app' : undefined,
    environment: needsEnvironment(scopeKey) && !values.environment ? 'Pick the environment' : undefined,
    platform: needsPlatform(scopeKey) && !values.platform ? 'Pick the platform' : undefined,
  };
}

const NO_SCOPE_ERRORS = { app: undefined, environment: undefined, platform: undefined };

/** Local validation: key, visibility, scope pieces and value (new variables only), description length. */
export function validateEnvVarForm(values: EnvVarFormValues, editing: EnvVar | undefined): { errors: EnvVarErrors; invalid: boolean } {
  const creating = !editing;
  const errors: EnvVarErrors = {
    key: keyError(values.key),
    visibility: visibilityError(values, editing),
    ...(creating ? scopeErrors(values) : NO_SCOPE_ERRORS),
    value: creating && !values.value.value && !values.value.base64 ? 'A value is required' : undefined,
    description: values.description.length > 500 ? 'Keep it under 500 characters' : undefined,
  };
  return { errors, invalid: Object.values(errors).some(Boolean) };
}

/** Edit sends metadata only: the value changes through a new version. An empty description clears it. */
export const buildPatchBody = (values: EnvVarFormValues) => ({
  description: values.description.trim() || null,
  visibility: values.visibility,
  valueType: values.valueType,
  configTime: values.configTime,
});

/** The scope pieces of a create body: only the ids the chosen scope needs (organization scope has no project). */
function scopeBody(values: EnvVarFormValues, projectId: string, apps: App[] | undefined): Partial<EnvVarCreateInput> {
  const { scopeKey } = values;
  const app = apps?.find((a) => a.slug === values.appSlug);
  return {
    ...(scopeKey === 'org' ? {} : { projectId }),
    ...(needsApp(scopeKey) && app ? { appId: app.id } : {}),
    ...(needsEnvironment(scopeKey) && values.environment ? { environment: values.environment } : {}),
    ...(needsPlatform(scopeKey) && values.platform ? { platform: values.platform } : {}),
  };
}

/** Create body: scope, key, metadata, the value (base64 for a picked file) and the trimmed optional description/note. */
export function buildCreateBody(values: EnvVarFormValues, projectId: string, apps: App[] | undefined): EnvVarCreateInput {
  return {
    ...scopeBody(values, projectId, apps),
    key: values.key,
    visibility: values.visibility,
    valueType: values.valueType,
    configTime: values.configTime,
    ...(values.value.base64 ? { valueBase64: values.value.base64 } : { value: values.value.value }),
    ...(values.description.trim() ? { description: values.description.trim() } : {}),
    ...(values.note.trim() ? { note: values.note.trim() } : {}),
  };
}

/** The part of a create result the toasts read. */
export interface CreateResultSummary {
  envVar: { key: string };
  version: { version: number; valueFingerprint: string };
  approval?: unknown;
  warnings: Array<{ code: string }>;
}

/** Toast description after a create: pending approval, or the version and fingerprint. */
export const createdDescription = (result: CreateResultSummary): string => (result.approval ? 'Pending approval — not effective until approved' : `v${result.version.version} · ${result.version.valueFingerprint}`);

/** Title and description of the warning toast, or `undefined` when the API raised none. */
export function warningToastOf(result: CreateResultSummary): { title: string; description: string } | undefined {
  const n = result.warnings.length;
  if (n === 0) return undefined;
  return { title: `${n} warning${n === 1 ? '' : 's'} on ${result.envVar.key}`, description: result.warnings.map((w) => w.code).join(' · ') };
}
